import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function AuthModal({ open, onClose }) {
  const { setUser } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");
    setBusy(true);
    try {
      if (mode === "forgot") {
        const { data } = await api.post("/auth/forgot-password", { email: form.email });
        setNotice(data.message || "If an account exists for that email, a reset link is on its way.");
        return;
      }
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const payload =
        mode === "login"
          ? { email: form.email, password: form.password }
          : form;
      const { data } = await api.post(path, payload);
      setUser(data.user);
      onClose();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
      redirectUrl
    )}`;
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/80 backdrop-blur-md px-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        data-testid="auth-modal-overlay"
      >
        <motion.div
          className="relative w-full max-w-md border border-white/10 bg-[#0A0A0A] p-8"
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 20, opacity: 0 }}
          transition={{ type: "spring", stiffness: 260, damping: 26 }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            className="absolute right-4 top-4 text-white/50 hover:text-white transition-colors"
            data-testid="auth-close-btn"
          >
            <X size={20} />
          </button>
          <p className="font-mono-imn text-xs tracking-[0.3em] text-gold uppercase mb-2">
            {mode === "login" ? "Welcome back" : mode === "register" ? "Join the network" : "Account recovery"}
          </p>
          <h2 className="font-display text-4xl font-bold uppercase tracking-tight mb-6">
            {mode === "login" ? "Sign In" : mode === "register" ? "Create Account" : "Forgot Password"}
          </h2>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <input
                data-testid="auth-name-input"
                className="w-full bg-white/5 border border-white/10 px-4 py-3 text-sm outline-none focus:border-gold transition-colors"
                placeholder="Full name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            )}
            <input
              data-testid="auth-email-input"
              type="email"
              className="w-full bg-white/5 border border-white/10 px-4 py-3 text-sm outline-none focus:border-gold transition-colors"
              placeholder="Email address"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
            />
            {mode !== "forgot" && (
              <input
                data-testid="auth-password-input"
                type="password"
                className="w-full bg-white/5 border border-white/10 px-4 py-3 text-sm outline-none focus:border-gold transition-colors"
                placeholder="Password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
              />
            )}
            {mode === "login" && (
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => { setMode("forgot"); setError(""); setNotice(""); setForm({ ...form, password: "" }); }}
                  className="text-xs text-white/50 hover:text-gold transition-colors"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </button>
              </div>
            )}
            {error && (
              <p className="text-sm text-red-400" data-testid="auth-error">
                {error}
              </p>
            )}
            {notice && (
              <p className="text-sm text-emerald-400" data-testid="auth-notice">
                {notice}
              </p>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="auth-submit-btn"
              className="w-full bg-white text-black font-semibold py-3 text-sm uppercase tracking-wider hover:bg-white/90 active:scale-[0.98] transition-all disabled:opacity-50"
            >
              {busy ? "Please wait…" : mode === "login" ? "Sign In" : mode === "register" ? "Sign Up" : "Send Reset Link"}
            </button>
          </form>

          {mode !== "forgot" && (
            <>
              <div className="flex items-center gap-3 my-5">
                <div className="h-px flex-1 bg-white/10" />
                <span className="font-mono-imn text-[10px] tracking-widest text-white/40">
                  OR
                </span>
                <div className="h-px flex-1 bg-white/10" />
              </div>

              <button
                onClick={googleLogin}
                data-testid="google-login-btn"
                className="w-full border border-white/20 py-3 text-sm uppercase tracking-wider hover:bg-white/5 transition-colors"
              >
                Continue with Google
              </button>
            </>
          )}

          <p className="text-center text-sm text-white/50 mt-6">
            {mode === "forgot" ? (
              <button
                onClick={() => { setMode("login"); setError(""); setNotice(""); setForm({ ...form, password: "" }); }}
                className="text-gold hover:underline"
                data-testid="auth-back-to-login"
              >
                Back to sign in
              </button>
            ) : (
              <>
                {mode === "login" ? "New to IMN?" : "Already a member?"}{" "}
                <button
                  onClick={() => {
                    setMode(mode === "login" ? "register" : "login");
                    setError("");
                    setNotice("");
                    setForm({ ...form, password: "" });
                  }}
                  className="text-gold hover:underline"
                  data-testid="auth-toggle-mode"
                >
                  {mode === "login" ? "Create account" : "Sign in"}
                </button>
              </>
            )}
          </p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
